import React from 'react';
import { Link } from 'react-router-dom';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Users,
  Heart,
  Globe,
  Shield,
  CheckCircle2,
  Building2,
  Briefcase,
  ArrowRight,
  BookOpen,
  ExternalLink,
} from 'lucide-react';

import Seo from '../components/Seo';

const LOGO_URL = "/inasw-logo.jpg";
const BOOK_IMAGE = "https://customer-assets.emergentagent.com/job_70b10f6b-9cd0-4369-aba2-66c0c28ea5fe/artifacts/nxmwrw6l_ChatGPT%20Image%20Feb%2020%2C%202026%2C%2005_57_23%20AM.png";
const AMAZON_BOOK_LINK = "https://a.co/d/0f4Uagyp";

const About = () => {
  return (
    <div className="min-h-screen bg-gray-50 pt-20">
      <Seo
        exactTitle="What is I Need A Social Worker? | Nationwide Social Work Career & Recruiting Platform"
        description="I Need A Social Worker (INASW) is the nationwide social work career and recruiting platform — social work jobs, employer recruiting, internships, clinical supervision, licensing information and professional resources. Learn what the platform is and where to start."
        path="/about"
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-br from-primary via-primary-hover to-primary text-white">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16 md:py-20">
          <div className="max-w-3xl">
            <Badge className="bg-secondary text-white mb-4">
              <Heart className="w-3 h-3 mr-1" /> About Us
            </Badge>
            <h1 className="font-serif text-4xl md:text-5xl font-bold mb-6">
              I Need A Social Worker
            </h1>
            <p className="text-xl text-white/90 leading-relaxed">
              The Nationwide Social Work Career &amp; Recruiting Platform — jobs, employer recruiting, internships, clinical supervision, licensing and professional resources, created exclusively for the social work profession.
            </p>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid lg:grid-cols-3 gap-12">
          {/* Main Text */}
          <div className="lg:col-span-2 space-y-8">
            {/* Mission */}
            <section>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">What is I Need A Social Worker?</h2>
              <p className="text-gray-600 leading-relaxed text-lg mb-4">
                <strong>I Need A Social Worker</strong> (INASW) is a nationwide social work career and recruiting platform.
                Employers use it to recruit and hire licensed social workers, and social workers use it to find jobs,
                internships, clinical supervisors, licensing information and professional resources — all in one place,
                focused entirely on the social work profession.
              </p>
              <p className="text-gray-600 leading-relaxed text-lg">
                It is a careers, recruiting and professional-resource platform — not an emergency referral service or crisis hotline.
                Our mission is to connect licensed social workers with employers who value ethical hiring, professional
                standards, and quality care.
              </p>
              <div className="flex flex-wrap gap-2 mt-5" data-testid="brand-internal-links">
                {[
                  ['Social Work Jobs', '/jobs'],
                  ['For Employers', '/for-employers'],
                  ['Internships', '/internships'],
                  ['Clinical Supervisors', '/find-a-supervisor'],
                  ['Licensing Center', '/license-renewal-center'],
                  ['Professional Resources', '/community/resources'],
                ].map(([label, to]) => (
                  <Link key={to} to={to} className="px-4 py-2 rounded-full text-sm bg-primary/5 text-primary font-medium hover:bg-primary/10 transition-colors">{label}</Link>
                ))}
              </div>
            </section>

            {/* What We Do */}
            <section>
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-4">What We Do</h2>
              <p className="text-gray-600 leading-relaxed text-lg">
                We simplify the hiring process by focusing only on social work—eliminating the clutter of generic job 
                boards and providing a space where qualified professionals and reputable employers can connect efficiently.
              </p>
            </section>

            {/* Values */}
            <section className="bg-white rounded-xl p-8 border border-gray-100">
              <h2 className="font-serif text-2xl font-bold text-gray-900 mb-6">Our Values</h2>
              <div className="grid sm:grid-cols-2 gap-6">
                {[
                  { icon: Shield, title: "Ethical Hiring", desc: "Promoting professional standards and quality care" },
                  { icon: Globe, title: "Nationwide Access", desc: "Creating visibility and career connections across the country" },
                  { icon: Users, title: "Inclusivity", desc: "Welcoming professionals of all backgrounds" },
                  { icon: CheckCircle2, title: "Equal Opportunity", desc: "Regardless of race, gender, ethnicity, or experience" },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-4">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                      <item.icon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h4 className="font-semibold text-gray-900">{item.title}</h4>
                      <p className="text-sm text-gray-600">{item.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Goal */}
            <section>
              <p className="text-gray-600 leading-relaxed text-lg">
                Our goal is to uplift the profession by creating access, visibility, and meaningful career 
                connections nationwide.
              </p>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Powered By Card */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <div className="flex items-center gap-3 mb-4">
                <img src={LOGO_URL} alt="INASW Logo" className="w-12 h-12 rounded-full object-cover ring-2 ring-secondary/50 shadow-sm" />
                <div>
                  <p className="text-sm text-gray-500">Powered by</p>
                  <p className="font-semibold text-gray-900">DSWS</p>
                </div>
              </div>
              <h3 className="font-serif text-lg font-bold text-gray-900 mb-3">
                Diversified Social Work Solutions
              </h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Diversified Social Work Solutions (DSWS) is committed to strengthening and supporting the social 
                work community through inclusive workforce initiatives and profession-focused products.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                We welcome applicants of all backgrounds, including race, gender, ethnicity, and experience level, 
                and promote equal opportunity across the profession.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed">
                In addition to workforce solutions, DSWS develops tools, resources, and products created specifically 
                for social workers to help advance ethical practice and professional growth.
              </p>
            </div>

            {/* Book Promotion Card */}
            <div className="bg-gradient-to-br from-primary/5 to-secondary/5 rounded-xl p-6 border border-gray-100">
              <Badge className="bg-secondary/10 text-secondary mb-4">
                <BookOpen className="w-3 h-3 mr-1" /> New Release
              </Badge>
              <img 
                src={BOOK_IMAGE} 
                alt="Home Health & Hospice Travel Log" 
                className="w-full rounded-lg shadow-lg mb-4"
              />
              <h3 className="font-serif text-lg font-bold text-gray-900 mb-2">
                Home Health & Hospice Travel Log
              </h3>
              <p className="text-xs text-gray-500 mb-3">
                A Professional Mileage & Visit Tracking Journal for Mobile Healthcare Providers
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Created for home health and hospice professionals, this structured travel log allows you to record 
                mileage, visit locations, dates, and essential travel details in one organized place.
              </p>
              <a 
                href={AMAZON_BOOK_LINK}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 w-full"
              >
                <Button className="w-full bg-secondary hover:bg-secondary-hover text-white">
                  Buy on Amazon <ExternalLink className="w-4 h-4 ml-1" />
                </Button>
              </a>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-xl p-6 border border-gray-100">
              <h4 className="font-semibold text-gray-900 mb-4">Quick Links</h4>
              <div className="space-y-2">
                <Link to="/jobs" className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
                  <Briefcase className="w-4 h-4" /> Browse All Jobs
                </Link>
                <Link to="/for-employers" className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
                  <Building2 className="w-4 h-4" /> For Employers
                </Link>
                <Link to="/internships" className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
                  <Briefcase className="w-4 h-4" /> Internships
                </Link>
                <Link to="/find-a-supervisor" className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
                  <Users className="w-4 h-4" /> Clinical Supervisors
                </Link>
                <Link to="/license-renewal-center" className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
                  <Shield className="w-4 h-4" /> Licensing Center
                </Link>
                <Link to="/community/resources" className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
                  <Heart className="w-4 h-4" /> Professional Resources
                </Link>
                <Link to="/contact" className="flex items-center gap-2 text-gray-600 hover:text-primary transition-colors">
                  <Heart className="w-4 h-4" /> Contact Us
                </Link>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* CTA Section */}
      <div className="bg-primary py-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-serif text-3xl font-bold text-white mb-4">
            Ready to Join Our Community?
          </h2>
          <p className="text-xl text-white/80 mb-8">
            Whether you're a social worker looking for opportunities or an employer seeking qualified professionals
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/register">
              <Button className="bg-secondary hover:bg-secondary-hover text-white h-12 px-8">
                Create Account <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/jobs">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10 h-12 px-8">
                Browse All Jobs
              </Button>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

export default About;
